import totalOffsetTop from "../helpers/totalOffsetTop";

const debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

class FilterDrawer extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", (e) => {
      if (!this.summary.contains(e.target)) return;

      e.preventDefault();

      if (this._allowToggle) {
        this.details.open ? this.close() : this.open();
      }
    });

    this.lastAllowToggle = null;

    const toggleOpen = () => {
      if (!this._allowToggle) {
        this._forceOpen();
        this.summary.tabIndex = -1;
      } else if (this.lastAllowToggle === false && this._allowToggle) {
        this._forceClosed();
        this.summary.removeAttribute("tabindex");
      }

      this.lastAllowToggle = this._allowToggle;
    };

    toggleOpen();

    this.onResize = debounce(toggleOpen, 200);

    window.addEventListener("resize", this.onResize);

    this.addEventListener("click", (e) => {
      if (!e.target.closest("[data-close]")) return;

      if (this._allowToggle && e.target.closest("filter-drawer") === this) {
        this.close();
      }
    });

    this.addEventListener("filter-drawer:force-closed", () => {
      if (this._allowToggle) {
        this._forceClosed();
      }
    });

    this.addEventListener("filter-drawer:close", () => {
      if (this._allowToggle) {
        this.close();
      }
    });

    this.handleDocumentClick = (e) => {
      if (this.details.open && this._allowToggle && !this.contains(e.target)) {
        this.close();
      }
    };

    this.handleKeyUp = (e) => {
      if (
        this.details.open &&
        this._allowToggle &&
        e.code.toUpperCase() === "ESCAPE"
      ) {
        this.close();
      }
    };

    document.addEventListener("keyup", this.handleKeyUp);
    document.addEventListener("click", this.handleDocumentClick);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.onResize);
    document.removeEventListener("keyup", this.handleKeyUp);
    document.removeEventListener("click", this.handleDocumentClick);
  }

  _forceOpen() {
    this.removeEventListener("transitionend", this.handleTransitionEnd);
    this.details.classList.add("open");
    this.details.open = true;
  }

  _forceClosed() {
    this.removeEventListener("transitionend", this.handleTransitionEnd);
    this.details.classList.remove("open");
    this.details.open = false;
  }

  open() {
    if (this.details.open) return;
    this.summary.classList.add("is-active");
    this.removeEventListener("transitionend", this.handleTransitionEnd);

    this.handleTransitionEnd = () => {
      this.removeEventListener("transitionend", this.handleTransitionEnd);
      const targetListbox = this._targetListbox;
      if (targetListbox) {
        targetListbox.setAttribute("active", "");
      }
    };

    this.addEventListener("transitionend", this.handleTransitionEnd);

    if (this.scrollToTopOnOpen) {
      const offsetTop = totalOffsetTop(this);
      window.scrollTo({
        top:
          offsetTop -
          document.querySelector("sticky-header").getBoundingClientRect()
            .height,
      });
    }

    this.details.open = true;

    window.requestAnimationFrame(() => {
      this.details.classList.add("open");
    });
  }

  close() {
    if (!this.details.open) return;
    this.summary.classList.remove("is-active");
    if (this.content) {
      this.removeEventListener("transitionend", this.handleTransitionEnd);

      this.handleTransitionEnd = () => {
        this.removeEventListener("transitionend", this.handleTransitionEnd);
        this.details.open = false;
      };

      this.addEventListener("transitionend", this.handleTransitionEnd);

      const targetListbox = this._targetListbox;
      if (targetListbox) {
        targetListbox.removeAttribute("active");
      }

      this.details.classList.remove("open");
    } else {
      this.details.open = false;
      this.details.classList.remove("open");
    }
  }

  get _allowToggle() {
    return window.matchMedia(this._mediaQuery).matches;
  }

  get _mediaQuery() {
    return this.getAttribute("allow-toggle-when") || "all";
  }

  get _targetListbox() {
    return document.getElementById(this.getAttribute("target-listbox"));
  }

  get details() {
    return this.querySelector("details");
  }

  get content() {
    return this.querySelector("[data-content]");
  }

  get summary() {
    return this.querySelector("summary");
  }

  get scrollToTopOnOpen() {
    return this.hasAttribute("scroll-to-top-on-open");
  }
}

if (!customElements.get("filter-drawer")) {
  customElements.define("filter-drawer", FilterDrawer);
}
